// Curated global snorkel-spot dictionary. Users type a beach name and the
// picker autocompletes against this list first; anything not in the list
// is still accepted verbatim so lesser-known spots aren't blocked.
//
// The list is small on purpose — it's the "did you mean?" hint set, not
// an exhaustive catalog. Names come from local knowledge, iNat hotspots,
// and NPS / state park references. Each entry gets a coarse region tag so
// the picker can show a small "· region" clue after the name in the
// dropdown, which helps disambiguate the many "Hanauma"-type collisions.
//
// This file is imported both by the client picker and by any future
// server-side normalisation, so it stays in src/shared/.

export interface CuratedBeach {
  name: string;
  region: string;
  // Coordinates — populated for the popular spots so the Collection's
  // by-beach view can render a mini-map. Optional; free-text and
  // uncoded curated beaches fall back to a list-only card.
  lat?: number;
  lng?: number;
}

export const CURATED_BEACHES: CuratedBeach[] = [
  // Hawaiʻi — Big Island
  { name: "Two Step", region: "Big Island · Hōnaunau", lat: 19.4188, lng: -155.9124 },
  { name: "Kahaluʻu Beach Park", region: "Big Island · Kailua-Kona", lat: 19.5799, lng: -155.9691 },
  { name: "Manta Ray Point", region: "Big Island · Kailua-Kona", lat: 19.6220, lng: -155.9945 },
  { name: "Kealakekua Bay", region: "Big Island · Captain Cook", lat: 19.4791, lng: -155.9285 },
  { name: "Puakō", region: "Big Island · Kohala", lat: 19.9752, lng: -155.8489 },
  { name: "Mauna Kea Beach", region: "Big Island · Kohala", lat: 20.0016, lng: -155.8244 },
  { name: "Hāpuna Beach", region: "Big Island · Kohala", lat: 19.9930, lng: -155.8244 },
  { name: "ʻAnaehoʻomalu Bay", region: "Big Island · Kohala", lat: 19.9152, lng: -155.8875 },
  { name: "Kikaua Point", region: "Big Island · Kohala", lat: 19.8351, lng: -155.9107 },
  { name: "Kua Bay", region: "Big Island · Kekaha Kai", lat: 19.7940, lng: -156.0263 },
  { name: "Old Airport Beach", region: "Big Island · Kailua-Kona", lat: 19.6532, lng: -155.9945 },
  { name: "White Sands", region: "Big Island · Kailua-Kona", lat: 19.5851, lng: -155.9678 },
  { name: "Honokōhau", region: "Big Island · Kailua-Kona", lat: 19.6771, lng: -156.0250 },
  { name: "Waiʻōpae Tide Pools", region: "Big Island · Kapoho", lat: 19.5031, lng: -154.8143 },
  { name: "Richardson Ocean Park", region: "Big Island · Hilo", lat: 19.7375, lng: -155.0308 },
  { name: "Carlsmith Beach Park", region: "Big Island · Hilo", lat: 19.7351, lng: -155.0364 },

  // Hawaiʻi — Maui
  { name: "Molokini Crater", region: "Maui", lat: 20.6321, lng: -156.4959 },
  { name: "Honolua Bay", region: "Maui", lat: 21.0136, lng: -156.6386 },
  { name: "Kāʻanapali Beach", region: "Maui", lat: 20.9247, lng: -156.6969 },
  { name: "Nāpili Bay", region: "Maui", lat: 20.9963, lng: -156.6659 },
  { name: "Kapalua Bay", region: "Maui", lat: 21.0016, lng: -156.6669 },
  { name: "Ulua Beach", region: "Maui · Wailea", lat: 20.6864, lng: -156.4438 },
  { name: "Kamaʻole Beach III", region: "Maui · Kīhei", lat: 20.7188, lng: -156.4506 },
  { name: "Makena Landing", region: "Maui", lat: 20.6577, lng: -156.4443 },
  { name: "Ahihi-Kīnaʻu", region: "Maui", lat: 20.6198, lng: -156.4335 },
  { name: "Black Rock", region: "Maui · Kāʻanapali", lat: 20.9270, lng: -156.6969 },
  { name: "Slaughterhouse Beach", region: "Maui", lat: 21.0207, lng: -156.6413 },
  { name: "Olowalu", region: "Maui", lat: 20.8143, lng: -156.6247 },

  // Hawaiʻi — Oʻahu
  { name: "Hanauma Bay", region: "Oʻahu", lat: 21.2690, lng: -157.6938 },
  { name: "Shark's Cove", region: "Oʻahu · North Shore", lat: 21.6519, lng: -158.0616 },
  { name: "Three Tables", region: "Oʻahu · North Shore", lat: 21.6506, lng: -158.0628 },
  { name: "Waimea Bay", region: "Oʻahu · North Shore", lat: 21.6421, lng: -158.0674 },
  { name: "Kuilima Cove", region: "Oʻahu · Turtle Bay", lat: 21.7071, lng: -157.9970 },
  { name: "Electric Beach", region: "Oʻahu · Kahe Point", lat: 21.3577, lng: -158.1266 },
  { name: "Makaha Beach", region: "Oʻahu", lat: 21.4744, lng: -158.2210 },
  { name: "Sans Souci", region: "Oʻahu · Waikīkī", lat: 21.2648, lng: -157.8232 },
  { name: "Queen's Beach", region: "Oʻahu · Waikīkī", lat: 21.2743, lng: -157.8271 },
  { name: "Turtle Canyon", region: "Oʻahu", lat: 21.2758, lng: -157.8168 },
  { name: "Kokololio", region: "Oʻahu", lat: 21.6117, lng: -157.9163 },
  { name: "Lanikai Beach", region: "Oʻahu", lat: 21.3924, lng: -157.7154 },

  // Hawaiʻi — Kauaʻi
  { name: "Tunnels Beach", region: "Kauaʻi · Hāʻena", lat: 22.2263, lng: -159.5745 },
  { name: "Kēʻē Beach", region: "Kauaʻi · Hāʻena", lat: 22.2225, lng: -159.5824 },
  { name: "Poʻipū Beach", region: "Kauaʻi", lat: 21.8720, lng: -159.4487 },
  { name: "Lāwaʻi Beach", region: "Kauaʻi", lat: 21.8814, lng: -159.4728 },
  { name: "Anini Beach", region: "Kauaʻi", lat: 22.2168, lng: -159.4429 },
  { name: "Salt Pond Beach", region: "Kauaʻi", lat: 21.8991, lng: -159.6006 },
  { name: "Makua Beach", region: "Kauaʻi · Tunnels", lat: 22.2263, lng: -159.5745 },

  // Mexico — Baja / Caribbean
  { name: "Cabo Pulmo", region: "Baja California Sur" },
  { name: "Los Arcos", region: "Puerto Vallarta" },
  { name: "El Cielo", region: "Cozumel" },
  { name: "Palancar Reef", region: "Cozumel" },
  { name: "Akumal Bay", region: "Riviera Maya" },
  { name: "Yal-ku Lagoon", region: "Riviera Maya" },
  { name: "Xel-Há", region: "Riviera Maya" },

  // Caribbean
  { name: "Trunk Bay", region: "US Virgin Islands · St. John" },
  { name: "Buck Island", region: "US Virgin Islands · St. Croix" },
  { name: "Champagne Reef", region: "Dominica" },
  { name: "Klein Bonaire", region: "Bonaire" },
  { name: "1000 Steps", region: "Bonaire" },
  { name: "Salt Pier", region: "Bonaire" },
  { name: "Grand Cayman Stingray City", region: "Cayman Islands" },
  { name: "Kittiwake Wreck", region: "Cayman Islands" },
  { name: "Devil's Grotto", region: "Cayman Islands" },
  { name: "Playa Frans", region: "Curaçao" },
  { name: "Playa Kalki", region: "Curaçao" },
  { name: "Tobago Cays", region: "St. Vincent & the Grenadines" },
  { name: "Reef Bay", region: "US Virgin Islands · St. John" },

  // French Polynesia
  { name: "Bora Bora Lagoon", region: "French Polynesia" },
  { name: "Rangiroa Blue Lagoon", region: "French Polynesia" },
  { name: "Fakarava South Pass", region: "French Polynesia" },
  { name: "Mo'orea Stingray Sandbank", region: "French Polynesia" },

  // Australia / South Pacific
  { name: "Ningaloo Reef", region: "Western Australia" },
  { name: "Great Barrier Reef · Agincourt", region: "Queensland" },
  { name: "Fitzroy Island", region: "Queensland" },
  { name: "Lady Musgrave Island", region: "Queensland" },
  { name: "Beqa Lagoon", region: "Fiji" },
  { name: "Namena Marine Reserve", region: "Fiji" },
  { name: "Rainbow Reef", region: "Fiji · Vanua Levu" },

  // Southeast Asia
  { name: "Sipadan", region: "Malaysia" },
  { name: "Perhentian Kecil", region: "Malaysia" },
  { name: "Tioman Coral Island", region: "Malaysia" },
  { name: "Nusa Lembongan · Mangrove Point", region: "Bali" },
  { name: "Manta Point", region: "Nusa Penida" },
  { name: "Crystal Bay", region: "Nusa Penida" },
  { name: "Menjangan Island", region: "Bali" },
  { name: "Amed", region: "Bali" },
  { name: "Tulamben USS Liberty", region: "Bali" },
  { name: "Koh Tao · Shark Bay", region: "Thailand" },
  { name: "Similan Islands", region: "Thailand" },
  { name: "Apo Reef", region: "Philippines" },
  { name: "Balicasag Island", region: "Philippines · Bohol" },
  { name: "Moalboal · Pescador", region: "Philippines · Cebu" },
  { name: "Coron Bay", region: "Philippines · Palawan" },

  // Egypt / Red Sea
  { name: "Ras Muhammad", region: "Red Sea · Egypt" },
  { name: "Blue Hole", region: "Red Sea · Dahab" },
  { name: "Elphinstone Reef", region: "Red Sea · Marsa Alam" },
  { name: "Marsa Shagra", region: "Red Sea · Marsa Alam" },

  // Mediterranean
  { name: "Medes Islands", region: "Spain · Costa Brava" },
  { name: "Cabo de Palos", region: "Spain · Murcia" },
  { name: "Blue Lagoon Comino", region: "Malta" },

  // Central / South America
  { name: "Isla del Caño", region: "Costa Rica" },
  { name: "Isla Coiba", region: "Panama" },
  { name: "Isla de la Plata", region: "Ecuador" },
  { name: "Galápagos · Kicker Rock", region: "Ecuador" },
  { name: "Galápagos · Devil's Crown", region: "Ecuador" },
  { name: "Fernando de Noronha · Baía do Sancho", region: "Brazil" },

  // Florida & Continental US
  { name: "Molasses Reef", region: "Florida Keys" },
  { name: "Looe Key", region: "Florida Keys" },
  { name: "Dry Tortugas", region: "Florida Keys" },
  { name: "Peanut Island", region: "Florida · West Palm" },
  { name: "Blue Heron Bridge", region: "Florida · West Palm" },
  { name: "Crystal River", region: "Florida" },
  { name: "Buccaneer Bay", region: "Florida · Weeki Wachee" },
  { name: "La Jolla Cove", region: "California" },
];

// Normalize a beach name for comparison / de-dup. Trim whitespace, collapse
// runs of internal spaces, case-fold. Kept in shared/ so client and server
// pick the same key.
export function beachKey(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}

// Look up a curated beach by user-supplied name, tolerant of case /
// whitespace differences. Returns undefined for free-text beaches or
// misspellings. The Collection uses this for the mini-map and region
// chip; a miss just renders the card without those extras.
const CURATED_BY_KEY = new Map(
  CURATED_BEACHES.map((b) => [beachKey(b.name), b]),
);
export function findCuratedBeach(name: string): CuratedBeach | undefined {
  return CURATED_BY_KEY.get(beachKey(name));
}

// Resolve a trip beach to coordinates: a curated snorkel spot first, then —
// if the name isn't curated — a place the user has actually logged fish at
// (their visited locations, matched by name). Lets a real spot like "Pauoa
// Bay" plot on maps even though it isn't in the curated dictionary. Returns
// null when neither source has coordinates.
export function resolveBeachCoords(
  name: string,
  visited?: { name?: string; lat: number; lng: number }[],
): { lat: number; lng: number; region?: string } | null {
  const curated = findCuratedBeach(name);
  if (curated?.lat != null && curated?.lng != null) {
    return { lat: curated.lat, lng: curated.lng, region: curated.region };
  }
  const key = beachKey(name);
  for (const v of visited ?? []) {
    if (v.lat == null || v.lng == null) continue;
    if (beachKey(v.name ?? "") === key) return { lat: v.lat, lng: v.lng };
  }
  return null;
}

// Case-insensitive prefix + substring search over the curated list. Ranks
// prefix matches ahead of internal-substring matches; ties broken by name
// length (shorter is more general).
export function suggestBeaches(
  query: string,
  limit = 8,
): CuratedBeach[] {
  const q = query.trim().toLowerCase();
  if (!q) return CURATED_BEACHES.slice(0, limit);
  const prefix: CuratedBeach[] = [];
  const substr: CuratedBeach[] = [];
  for (const b of CURATED_BEACHES) {
    const n = b.name.toLowerCase();
    if (n.startsWith(q)) prefix.push(b);
    else if (n.includes(q)) substr.push(b);
  }
  const byLen = (a: CuratedBeach, b: CuratedBeach) =>
    a.name.length - b.name.length;
  prefix.sort(byLen);
  substr.sort(byLen);
  return [...prefix, ...substr].slice(0, limit);
}
